/* eslint-disable jsx-a11y/alt-text */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faPlus,
  faMinus,
  faTags,
  faClock,
  faTools,
  faCode,
  faProjectDiagram,
  faDatabase,
  faBroadcastTower,
  faCloud,
  faIndustry,
  faBolt,
  faChartLine,
  faWrench,
  faBoxes,
  faBarcode,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

import img1 from '../../../assets/img/Product/Gui_builder/unified_icon.png';
import img2 from '../../../assets/img/Product/Gui_builder/builder_icon.png';
import img3 from '../../../assets/img/Product/Gui_builder/openess_icon.png';
import img4 from '../../../assets/img/Product/Gui_builder/support.jpg';

import nodeflowLogo from '../../../assets/img/Product/NodeFlow/app_icon.png';
import nodeflowHero from '../../../assets/img/Product/NodeFlow/nodeflow.png';
import nodeEditor from '../../../assets/img/Product/NodeFlow/node_editor.svg';
import nodeflowIntegration from '../../../assets/img/Product/NodeFlow/nodeflow_integration.svg';
import nodeflowCategories from '../../../assets/img/Product/NodeFlow/nodeflow_categories.svg';
import guibuilderNodfloArchitecture from '../../../assets/img/Product/NodeFlow/guibuilder_nodflo_architecture.svg';
import cloudviewNodfloArchitecture from '../../../assets/img/Product/NodeFlow/cloudview_nodflo_architecture.svg';

const NodeFlowPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = React.useState(0);

  const handleQuote = () => navigate('/quote');
  const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? -1 : idx);

  const pageTitle = 'NodFlo - Visual Workflow Orchestration | GUI View';
  const pageDescription =
    'NodFlo is the workflow orchestration layer that connects GUIBuilder and CloudView. Design, execute and monitor visual workflows with 80+ nodes for MQTT, REST API, database, scripting and real-time automation.';
  const pageKeywords =
    'NodFlo, workflow orchestration, visual programming, SCADA workflow, MES, EMS, MQTT, REST API, database, JavaScript scripting, industrial automation, GUIBuilder, CloudView';

  const benefits = [
    {
      icon: img1,
      title: 'Unified Infrastructure',
      text: 'Seamlessly bridges GUIBuilder Player screens and CloudView device data through a single workflow runtime.',
    },
    {
      icon: img2,
      title: 'Lightweight Runtime',
      text: 'Built on an efficient Express engine that starts only active workflows and batches real-time updates for minimal overhead.',
    },
    {
      icon: img3,
      title: 'Open Architecture',
      text: 'Connect to external MQTT brokers, REST services and SQL/NoSQL databases without vendor lock-in.',
    },
    {
      icon: img4,
      title: 'Production Support',
      text: 'Persist node state across restarts, import/export complete workflow databases and deploy as a packaged runtime.',
    },
  ];

  const nodeCategories = [
    {
      name: 'Tag Read / Write',
      icon: faTags,
      examples: 'inputVariable, outputVariable, datasource, datatag',
      color: '#2563eb',
    },
    {
      name: 'Triggers & Schedules',
      icon: faClock,
      examples: 'trigger, crontab, minutely, timer, booleanTrigger',
      color: '#d97706',
    },
    {
      name: 'Common Utilities',
      icon: faTools,
      examples: 'code, delay, merge, transform, mathOperationNode, jsonFilter',
      color: '#0891b2',
    },
    {
      name: 'Scripting',
      icon: faCode,
      examples: 'script, filter_script',
      color: '#db2777',
    },
    {
      name: 'Logical Operations',
      icon: faProjectDiagram,
      examples: 'andNode, orNode, notNode, xorNode',
      color: '#7c3aed',
    },
    {
      name: 'Database & Datasource',
      icon: faDatabase,
      examples: 'database, datasource, datatag',
      color: '#7c3aed',
    },
    {
      name: 'MQTT',
      icon: faBroadcastTower,
      examples: 'mqttBroker, mqttSubscribe, mqttPublish',
      color: '#ea580c',
    },
    {
      name: 'API Services',
      icon: faCloud,
      examples: 'api, geoIPNode, dataFilterNode',
      color: '#2563eb',
    },
    {
      name: 'MES / OEE',
      icon: faIndustry,
      examples: 'machine, oeeCalculator, shiftCalendar, databaseLogger',
      color: '#059669',
    },
    {
      name: 'EMS',
      icon: faBolt,
      examples: 'energyPlant, energyMeter, mesDatabaseLogger',
      color: '#f59e0b',
    },
    {
      name: 'Quality & SPC',
      icon: faChartLine,
      examples: 'qualityCheck, spcControlChart, ncrRecorder',
      color: '#65a30d',
    },
    {
      name: 'Maintenance',
      icon: faWrench,
      examples: 'pmSchedule, toolDieLife, maintenanceWorkOrder',
      color: '#0891b2',
    },
    {
      name: 'Inventory & WIP',
      icon: faBoxes,
      examples: 'wipTracker, rawMaterialIssue, finishedGoodsReceipt',
      color: '#7c3aed',
    },
    {
      name: 'Traceability',
      icon: faBarcode,
      examples: 'batchTracker, productionBatchLogger, materialConsumption',
      color: '#db2777',
    },
    {
      name: 'Production & Labor',
      icon: faUsers,
      examples: 'productionOrderStatus, laborClock, andonLight',
      color: '#2563eb',
    },
  ];

  const useCases = [
    {
      title: 'MES & OEE',
      icon: faIndustry,
      desc: 'Track machine states, downtime reasons and shift calendars to calculate OEE in real time.',
      color: '#059669',
    },
    {
      title: 'Energy Monitoring',
      icon: faBolt,
      desc: 'Read plant and meter data, aggregate consumption and log readings to the EMS database.',
      color: '#f59e0b',
    },
    {
      title: 'Batch Traceability',
      icon: faBarcode,
      desc: 'Follow production batches from raw material issue through finished-goods receipt.',
      color: '#db2777',
    },
    {
      title: 'Predictive Maintenance',
      icon: faWrench,
      desc: 'Trigger PM schedules, track tool/die life and generate maintenance work orders automatically.',
      color: '#0891b2',
    },
    {
      title: 'Quality & SPC',
      icon: faChartLine,
      desc: 'Record quality checks, reject counts and SPC control-chart values for compliance reporting.',
      color: '#65a30d',
    },
    {
      title: 'Web Dashboards',
      icon: faCloud,
      desc: 'Stream tag values over MQTT to CloudView dashboards for remote monitoring and control.',
      color: '#2563eb',
    },
  ];

  const faqs = [
    {
      q: 'Is NodFlo a standalone product?',
      a: 'No. NodFlo is a workflow orchestration module designed to connect GUIBuilder and CloudView. It automates logic, data pipelines and events between the HMI/SCADA screens built in GUIBuilder and the MQTT/API/device layer managed by CloudView.',
    },
    {
      q: 'How are workflows executed?',
      a: 'The NodFlo server instantiates a FlowEngine for every workflow marked active. The engine loads nodes and edges, calls start() on each node and dispatches values along edges. Node state is persisted in SQLite via better-sqlite3 and restored on restart.',
    },
    {
      q: 'Which databases are supported?',
      a: 'Workflow metadata is stored in SQLite. Node state is persisted in a better-sqlite3 database. External database nodes support SQLite, MySQL, PostgreSQL and SQL Server through dedicated drivers.',
    },
    {
      q: 'Can I write custom logic?',
      a: 'Yes. NodFlo provides four JavaScript nodes: code (multi-output function), script (long-running interval-based logic), transform (input transformation) and filter_script (conditional filtering).',
    },
    {
      q: 'How does real-time monitoring work?',
      a: 'The embedded Aedes MQTT broker streams node value updates to the React Flow editor over WebSockets. The UnifiedNodeManager batches updates at ~60 fps so the UI stays responsive even with large flows.',
    },
    {
      q: 'Can workflows be deployed to production?',
      a: 'Yes. Active workflows are started automatically when the server boots. You can export the entire workflow SQLite database and import it into another NodFlo runtime, including packaged binaries.',
    },
  ];

  const cardHover = {
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  };

  const cardHoverActive = {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
  };

  return (
    <>
      <Helmet>
        <title>GUI View</title>
        <meta name="og:title" content={pageTitle} />
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="og:description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="og:keywords" content={pageKeywords} />
      </Helmet>

      {/* Hero */}
      <div className="fancy-text-block-thirteen pt-140 md-pt-100">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <img
                  src={nodeflowLogo}
                  alt="NodFlo visual workflow designer application"
                />
                <h3 style={{ color: '#6c757d', fontSize: '22px' }}>
                  Visual Workflow Orchestration for Industrial Applications
                </h3>
                <br />
              </div>
              <p>
                NodFlo connects GUIBuilder and CloudView through a visual,
                node-based programming environment. Design data pipelines,
                automate application logic, handle events and execute workflows
                in real time — without writing traditional control code.
              </p>
              <br />
              <p>
                With 80+ ready-to-use nodes, an embedded MQTT broker,
                multi-database connectivity and JavaScript scripting, NodFlo
                turns complex MES, EMS and SCADA automation into drag-and-drop
                workflows.
              </p>
              <div className="md-pt-0 mt-10">
                <button
                  onClick={handleQuote}
                  style={{ marginBottom: '30px' }}
                  className="theme-btn-two float-lg-right"
                >
                  Request a Quote
                </button>
              </div>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <img
                src={nodeflowHero}
                alt="NodFlo visual workflow designer application"
                className="w-100 rounded-4 shadow"
                style={{ border: '1px solid #e2e8f0' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="fancy-feature-twentyFour pt-10 md-pt-5" id="benefits">
        <div className="container">
          <div className="text-center">
            <div
              className="title-style-two"
              data-aos="fade-up"
              data-aos-duration="100"
            >
              <h2>Benefits Of NodFlo</h2>
              <br />
            </div>
          </div>
        </div>
        <div className="bg-wrapper mt-0 md-mt-0">
          <div className="container">
            <div className="row">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="col-lg-6 d-flex mb-35"
                  data-aos="fade-up"
                  data-aos-duration="1200"
                  data-aos-delay={i % 2 === 0 ? '0' : '100'}
                >
                  <div className="block-style-twentyFour">
                    <div className="d-flex align-items-start">
                      <div className="icon d-flex align-items-center justify-content-center">
                        <img src={b.icon} alt="icon" />
                      </div>
                      <div className="text">
                        <h4>{b.title}</h4>
                        <p>{b.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* What is NodFlo / integration */}
      <div className="fancy-text-block-thirteen pt-20 md-pt-20">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <h2>The Bridge Between Design and Operations</h2>
                <br />
              </div>
              <p>
                NodFlo is a visual automation flow designer for building custom
                industrial logic without traditional coding. Using a
                drag-and-drop canvas, engineers assemble flows with nodes for
                triggers, logical decisions, conditions, parsing, data logging,
                MES, EMS and MQTT subscribe/publish — then execute them in real
                time.
              </p>
              <br />
              <p>
                <b>Module for GUIBuilder SCADA:</b> NodFlo connects directly to
                GUIBuilder Player as its automation module. It reads live tag
                values from running HMI/SCADA screens, runs the user-designed
                flow (logic, scripts, database actions, calculations) and writes
                tag values back whenever the application requires it — so
                operators see instant updates, alarms and animations on the
                plant floor.
              </p>
              <br />
              <p>
                <b>Module for CloudView IIoT Gateway:</b> NodFlo also plugs into
                CloudView as its workflow module. It subscribes to device data
                and MQTT topics, applies the same automation flows, and
                publishes processed results back to CloudView dashboards and
                remote systems for cloud monitoring and control.
              </p>
              <br />
              <p>
                Each connection is independent — NodFlo can automate GUIBuilder
                tags, CloudView devices, or both from one unified flow engine.
              </p>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <img
                src={nodeflowIntegration}
                alt="NodFlo sits between GUIBuilder and CloudView as an independent automation module for each product"
                className="w-100 rounded-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Visual editor */}
      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <h2>Drag-and-Drop Visual Editor</h2>
                <br />
              </div>
              <p>
                The NodFlo editor is built on React Flow and provides a
                professional canvas for designing workflows:
              </p>
              <br />
              <p>
                <b>Drag-and-drop node creation:</b> Pull nodes from the library
                and drop them onto the canvas.
              </p>
              <p>
                <b>Wire nodes:</b> Connect inputs and outputs with intuitive
                source/target handles and bezier wires.
              </p>
              <p>
                <b>Productivity shortcuts:</b> Undo/redo, copy/paste, delete,
                zoom, fit view, minimap and canvas lock.
              </p>
              <p>
                <b>Performance mode:</b> Large flows render only visible
                elements, keeping the editor smooth.
              </p>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <img
                src={nodeEditor}
                alt="NodFlo drag-and-drop editor with connected nodes"
                className="w-100 rounded-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Node library / categories */}
      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <h2>Extensive Node Library</h2>
                <br />
              </div>
              <p>
                NodFlo ships with 80+ nodes organized into 19 categories.
                Whether you need to read a tag, call an API, log to a database,
                run a script or calculate OEE, there is a node for the task.
              </p>
              <br />
              <p>
                Each node exposes a configuration dialog and an information
                panel, so you can set parameters and understand behavior without
                leaving the editor.
              </p>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <img
                src={nodeflowCategories}
                alt="NodFlo node library categories"
                className="w-100 rounded-4"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fancy-feature-twentyFour pt-20 md-pt-10">
        <div className="container">
          <div className="bg-wrapper mt-0 md-mt-0">
            <div className="container">
              <div className="row">
                {nodeCategories.map((cat, i) => (
                  <div
                    key={i}
                    className="col-lg-4 col-md-6 d-flex mb-30"
                    data-aos="fade-up"
                    data-aos-duration="1200"
                    data-aos-delay={(i % 3) * 100}
                  >
                    <div
                      className="block-style-twentyFour w-100"
                      style={cardHover}
                      onMouseEnter={(e) =>
                        Object.assign(e.currentTarget.style, cardHoverActive)
                      }
                      onMouseLeave={(e) =>
                        Object.assign(e.currentTarget.style, {
                          transform: 'translateY(0)',
                          boxShadow: 'none',
                        })
                      }
                    >
                      <div className="d-flex align-items-start">
                        <div
                          className="icon d-flex align-items-center justify-content-center me-3"
                          style={{
                            background: cat.color,
                            borderRadius: 12,
                            minWidth: 52,
                            height: 52,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={cat.icon}
                            style={{ color: '#fff', fontSize: 22 }}
                          />
                        </div>
                        <div className="text">
                          <h4>{cat.name}</h4>
                          <p className="mb-0">
                            <small>{cat.examples}</small>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Integrations: GUIBuilder and CloudView */}
      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <h2>GUIBuilder Integration</h2>
                <br />
              </div>
              <p>
                NodFlo extends the screens you build in GUIBuilder with
                intelligent backend logic. Instead of hard-coding behavior in
                every widget, you model the business logic once in NodFlo and
                bind GUIBuilder Player tags to the resulting values and events.
              </p>
              <br />
              <p>
                NodFlo continuously reads tag values from the running GUIBuilder
                Player application. When your flow decides an action is needed —
                for example, if a temperature exceeds a threshold — NodFlo
                writes the new value back to the Player so operators see instant
                updates, animations and alarms.
              </p>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <img
                src={guibuilderNodfloArchitecture}
                alt="GUIBuilder and NodFlo real-time integration architecture"
                className="w-100 h-100"
                style={{ display: 'block' }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="row align-items-center">
            <div
              className="col-lg-6 order-lg-first"
              data-aos="fade-right"
              data-aos-duration="1200"
            >
              <div className="title-style-two">
                <h2>CloudView Integration</h2>
                <br />
              </div>
              <p>
                NodFlo shares its runtime database path through CloudView,
                enabling a single source of truth for device tags, dashboards
                and workflow state.
              </p>
              <br />
              <p>
                The embedded MQTT broker — running on TCP 6000 and WebSocket
                8001 — lets CloudView dashboards subscribe to live workflow
                values, while CloudView-managed devices can trigger NodFlo
                workflows through MQTT topics. Processed results are published
                back to CloudView for remote monitoring and control.
              </p>
            </div>
            <div
              className="col-lg-6 col-md-8 m-auto order-lg-last"
              data-aos="fade-left"
              data-aos-duration="1200"
            >
              <img
                src={cloudviewNodfloArchitecture}
                alt="CloudView and NodFlo IIoT gateway architecture"
                className="w-100"
                style={{ display: 'block' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Feature grid */}
      <div className="fancy-feature-twentyFour pt-80 md-pt-80">
        <div className="container">
          <div className="text-center">
            <div
              className="title-style-two"
              data-aos="fade-up"
              data-aos-duration="1200"
            >
              <h2>Built for Industrial Connectivity</h2>
              <br />
            </div>
          </div>
        </div>
        <div className="bg-wrapper mt-0 md-mt-0">
          <div className="container">
            <div className="row">
              {[
                {
                  title: 'MQTT',
                  text: 'Embedded Aedes broker plus mqttBroker, mqttSubscribe and mqttPublish nodes for external brokers. Supports TLS, authentication and client certificates.',
                },
                {
                  title: 'REST API',
                  text: 'Workflow CRUD, broker management, tag settings and CloudView database connect endpoints exposed through Express. The api node calls external REST services.',
                },
                {
                  title: 'Database',
                  text: 'Store workflow metadata in SQLite, node state in better-sqlite3, and read/write MySQL, PostgreSQL and SQL Server through database and datasource nodes.',
                },
                {
                  title: 'JavaScript Scripting',
                  text: 'Write custom logic with code, script, transform and filter_script nodes. Use setInterval, send() and publish() to build long-running or one-shot behaviors.',
                },
                {
                  title: 'Real-Time Execution',
                  text: 'FlowEngine dispatches values along edges as soon as they are produced. UnifiedNodeManager batches live updates to the React Flow editor over MQTT.',
                },
                {
                  title: 'Event System',
                  text: 'Edge-driven data flow plus MQTT request/response topics. Nodes react to triggers, incoming messages, timer events and upstream value changes.',
                },
                {
                  title: 'Drag-and-Drop Editor',
                  text: 'React Flow canvas with node library, connection handles, undo/redo, copy/paste, delete, zoom, fit view, minimap and performance mode.',
                },
                {
                  title: 'Deployment',
                  text: 'Activate workflows to start them automatically, export/import the SQLite workflow database and deploy as a packaged NodFlo runtime.',
                },
              ].map((f, i) => (
                <div
                  key={i}
                  className="col-lg-6 d-flex mb-35"
                  data-aos="fade-up"
                  data-aos-duration="1200"
                  data-aos-delay={i % 2 === 0 ? '0' : '100'}
                >
                  <div className="block-style-twentyFour">
                    <div className="text">
                      <h4>{f.title}</h4>
                      <p>{f.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="text-center">
            <div
              className="title-style-two"
              data-aos="fade-up"
              data-aos-duration="1200"
            >
              <h2>Use Cases</h2>
              <br />
            </div>
          </div>
          <div className="row">
            {useCases.map((uc, i) => (
              <div
                key={i}
                className="col-lg-4 col-md-6 mb-30"
                data-aos="fade-up"
                data-aos-duration="1200"
                data-aos-delay={(i % 3) * 100}
              >
                <div
                  className="p-4 h-100 rounded-4 bg-white"
                  style={{
                    border: '1px solid #e2e8f0',
                    ...cardHover,
                  }}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, {
                      ...cardHoverActive,
                      borderColor: uc.color,
                    })
                  }
                  onMouseLeave={(e) =>
                    Object.assign(e.currentTarget.style, {
                      transform: 'translateY(0)',
                      boxShadow: 'none',
                      borderColor: '#e2e8f0',
                    })
                  }
                >
                  <div
                    className="d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 14,
                      background: `linear-gradient(135deg, ${uc.color}22 0%, ${uc.color}44 100%)`,
                      border: `2px solid ${uc.color}`,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={uc.icon}
                      style={{ color: uc.color, fontSize: 26 }}
                    />
                  </div>
                  <h4>{uc.title}</h4>
                  <p className="mb-0">{uc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="fancy-text-block-thirteen pt-80 md-pt-80">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center">
                <div
                  className="title-style-two"
                  data-aos="fade-up"
                  data-aos-duration="1200"
                >
                  <h2>Frequently Asked Questions</h2>
                  <br />
                </div>
              </div>
              <div data-aos="fade-up" data-aos-duration="1200">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="mb-3 rounded-3 overflow-hidden"
                    style={{ border: '1px solid #e2e8f0' }}
                  >
                    <button
                      className="w-100 d-flex justify-content-between align-items-center p-3 bg-white border-0"
                      onClick={() => toggleFaq(i)}
                      style={{ textAlign: 'left' }}
                    >
                      <strong>{faq.q}</strong>
                      <FontAwesomeIcon
                        icon={openFaq === i ? faMinus : faPlus}
                        style={{ color: '#2563eb' }}
                      />
                    </button>
                    {openFaq === i && (
                      <div className="p-3 bg-light">
                        <p className="mb-0">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <section
        className="bg-color-orange position-relative border-0 m-0 mt-80"
        style={{
          backgroundImage:
            'linear-gradient(to right, #501e9c 0%, #8169f1 30%, #8169f1 30%, #a44cee 73%, #8d3d86 100%)',
          paddingTop: '50px',
          paddingBottom: '50px',
        }}
      >
        <div className="container">
          <div className="row text-white text-center align-items-center">
            <div className="col-md-6">
              <h2
                style={{ fontSize: '25px', color: 'white', marginBottom: '0' }}
              >
                Start orchestrating your workflows today
              </h2>
            </div>
            <div className="col-md-6 mt-4">
              <div className="d-flex flex-column align-items-center">
                <Link
                  to="/contact-us"
                  className="btn"
                  style={{
                    backgroundColor: '#000',
                    color: 'white',
                    height: '45px',
                    width: '70%',
                    fontSize: '15px',
                    border: 'none',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Talk with our experts
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ marginLeft: '8px' }}
                  />
                </Link>
                <Link
                  to="/quote"
                  className="btn"
                  style={{
                    backgroundColor: '#000',
                    color: 'white',
                    height: '45px',
                    width: '70%',
                    fontSize: '15px',
                    border: 'none',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Request a Quote
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ marginLeft: '8px' }}
                  />
                </Link>
                <Link
                  to="/software"
                  className="btn"
                  style={{
                    backgroundColor: '#000',
                    color: 'white',
                    height: '45px',
                    width: '70%',
                    fontSize: '15px',
                    border: 'none',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Download product
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ marginLeft: '8px' }}
                  />
                </Link>
                <Link
                  to="/tutorial"
                  className="btn"
                  style={{
                    backgroundColor: '#000',
                    color: 'white',
                    height: '45px',
                    width: '70%',
                    fontSize: '15px',
                    border: 'none',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Video tutorial
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    style={{ marginLeft: '8px' }}
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NodeFlowPage;
